import { claimsModalEditingModes } from '../claimsModal/claimsModal.constants';
import { castToBoolean } from '../../../../../lib/util';

const viewEventDetailsAdminPath = '/events/summary.php?ev_id=';

class PositionsPanelsController {
  constructor(
    $q,
    $scope,
    $translate,
    moment,
    LogService,
    APIService,
    DateTimeService,
    PositionSearchService
  ) {
    "ngInject";
    this.$q = $q;
    this.$scope = $scope;
    this.$translate = $translate;
    this.moment = moment;
    this.LogService = LogService;
    this.APIService = APIService;
    this.DateTimeService = DateTimeService;
    this.PositionSearchService = PositionSearchService;

    this._currentlyExpandedPanelIndex = 0;

    this._lastPositionsByGroup = {};
  }

  $onInit() {
    this._updatePanelTitles();
    this.$scope.$on('LanguageServiceChangedLanguage', () => {
      this._updatePanelTitles();
    });

    if (this.rejectedPositions.length > 0){
      this._currentlyExpandedPanelIndex = 3;
    }
  }

  set staffUser(staffUser) {
    this._staffUser = staffUser;
  }

  get staffUser() {
    return this._staffUser;
  }

  get positionsByGroup() {
    const appliedFilteredPositionsByGroup = (this.PositionSearchService && this.PositionSearchService.appliedFilteredPositionsByGroup);
    if (appliedFilteredPositionsByGroup && appliedFilteredPositionsByGroup !== this._lastPositionsByGroup) {
      // Update!
      this._lastPositionsByGroup = appliedFilteredPositionsByGroup;
      this._updatePanelTitles();
    } else {
    }
    return this._lastPositionsByGroup;
  }

  get currentPositions() {
    return this.positionsByGroup.current;
  }

  get upcomingPositions() {
    return this.positionsByGroup.upcoming;
  }

  get upcomingPositionsTitle() {
    return this._upcomingPositionsTitle;
  }

  get previousPositionsTitle() {
    return this._previousPositionsTitle;
  }

  get previousPositions() {
    return this.positionsByGroup.previous;
  }

  get rejectedPositionsTitle() {
    return this._rejectedPositionsTitle;
  }

  get rejectedPositions() {
    return this.positionsByGroup.rejected;
  }

  get availablePositions() {
    return this.positionsByGroup.available;
  }

  get availablePositionsTitle() {
    return this._availablePositionsTitle;
  }

  /**
   * When merchandising is enabled, the dashboard groups activities by
   * completion status (Rejected / Incomplete / Complete / Available) instead of
   * the default date-based panels.
   */
  get useStatusBuckets() {
    return !!(this.staffUser && this.staffUser.merchandising_enabled);
  }

  get incompletePositions() {
    return this.positionsByGroup.incomplete || [];
  }

  get incompletePositionsTitle() {
    return this._incompletePositionsTitle;
  }

  get completePositions() {
    return this.positionsByGroup.complete || [];
  }

  get completePositionsTitle() {
    return this._completePositionsTitle;
  }

  _isRejectedResponse(response){
    return response.recapResponse && response.recapResponse.status_id === '3';
  }

  _updatePanelTitles() {
    const useStatus = this.useStatusBuckets;
    const upcomingPositionsKey = this.upcomingPositions.length === 1 ? 'UPCOMING_POSITION' : 'UPCOMING_POSITIONS';
    const previousPositionsKey = this.previousPositions.length === 1 ? 'PREVIOUS_POSITION' : 'PREVIOUS_POSITIONS';
    // Rejected/available panels reword to "Activities" in the merchandising view.
    const rejectedPositionsKey = useStatus
      ? (this.rejectedPositions.length === 1 ? 'REJECTED_ACTIVITY' : 'REJECTED_ACTIVITIES')
      : (this.rejectedPositions.length === 1 ? 'REJECTED_RECAP' : 'REJECTED_RECAPS');
    const availablePositionsKey = useStatus
      ? (this.availablePositions.length === 1 ? 'AVAILABLE_ACTIVITY' : 'AVAILABLE_ACTIVITIES')
      : (this.availablePositions.length === 1 ? 'AVAILABLE_POSITION' : 'AVAILABLE_POSITIONS');
    const incompletePositionsKey = this.incompletePositions.length === 1 ? 'INCOMPLETE_ACTIVITY' : 'INCOMPLETE_ACTIVITIES';
    const completePositionsKey = this.completePositions.length === 1 ? 'COMPLETE_ACTIVITY' : 'COMPLETE_ACTIVITIES';
    const syncWindowDefinition = this.staffUser.syncWindowDefinition;
    const throughKey = 'THROUGH';
    const fromKey = 'FROM';

    // English fallbacks for the merchandising bucket keys, which may not exist
    // in every tenant's server-provided translations (angular-translate returns
    // the key itself when a translation is missing).
    const fallbacks = {
      REJECTED_ACTIVITY: 'Rejected Activity',
      REJECTED_ACTIVITIES: 'Rejected Activities',
      INCOMPLETE_ACTIVITY: 'Incomplete Activity',
      INCOMPLETE_ACTIVITIES: 'Incomplete Activities',
      COMPLETE_ACTIVITY: 'Complete Activity',
      COMPLETE_ACTIVITIES: 'Complete Activities',
      AVAILABLE_ACTIVITY: 'Available Activity',
      AVAILABLE_ACTIVITIES: 'Available Activities',
    };

    const throughDate = this.moment()
      .add(syncWindowDefinition.params.shifts_days_forward, 'days')
      .format('MMM D');

    const fromDate = this.moment()
      .add(syncWindowDefinition.params.shifts_days_back * -1, 'days')
      .format('MMM D');

    return this.$q.when(this.$translate([upcomingPositionsKey, previousPositionsKey, rejectedPositionsKey, availablePositionsKey, incompletePositionsKey, completePositionsKey, throughKey, fromKey]))
      .then(translations => {
        const label = key => {
          const translated = translations[key];
          return (!translated || translated === key) ? (fallbacks[key] || key) : translated;
        };
        let fromText = translations[fromKey] ? translations[fromKey].toLowerCase() : 'from';
        let throughText = translations[throughKey] ? translations[throughKey].toLowerCase() : 'through';

        this._upcomingPositionsTitle = `${this.upcomingPositions.length} ${translations[upcomingPositionsKey]}`;
        if (syncWindowDefinition.params.shifts_days_forward > 0) {
          this._upcomingPositionsTitle += ` (${throughText} ${throughDate})`;
        }

        this._previousPositionsTitle = `${this.previousPositions.length} ${translations[previousPositionsKey]}`;
        if (syncWindowDefinition.params.shifts_days_back > 0) {
          this._previousPositionsTitle += ` (${fromText} ${fromDate})`;
        }

        this._rejectedPositionsTitle = `${this.rejectedPositions.length} ${label(rejectedPositionsKey)}`;

        this._availablePositionsTitle = `${this.availablePositions.length} ${label(availablePositionsKey)}`;

        this._incompletePositionsTitle = `${this.incompletePositions.length} ${label(incompletePositionsKey)}`;

        this._completePositionsTitle = `${this.completePositions.length} ${label(completePositionsKey)}`;
        if (syncWindowDefinition.params.shifts_days_back > 0) {
          this._completePositionsTitle += ` (${fromText} ${fromDate})`;
        }
      });
  }

  collapsedByIndex(idx) {
    return idx !== this._currentlyExpandedPanelIndex;
  }

  toggleCollapsedByIndex(idx) {
    if (this.collapsedByIndex(idx)) {
      // If this node is collapsed, expand it
      this._currentlyExpandedPanelIndex = idx;
    } else {
      // If this node is expanded, set expanded to -1 (no nodes expanded)
      this._currentlyExpandedPanelIndex = -1;
    }
  }
}

export default PositionsPanelsController;
